import {
  DTRetailer_Public,
  FBD_Retailer_Public,
  DTVendable,
  FBD_Vendable,
  DTCategory_Public,
  FBD_Category_Public,
  DTHoldingProxy,
  FBD_HoldingProxy,
} from '@rabbit/data/types';
import {
  SearchOptions,
  //SearchResultsFacet,
  SearchResults,
  EnhancedSearchResults,
} from './types';
import {
  SearchGeneric,
  //SearchForFacetValues,
} from './internal';

/**
 * Searches for categories.
 * @param query search query test e.g. 'umbrella'
 * @returns an array of categories
 * @example
 * SearchCategories('umbrella')
 */
export async function SearchCategories(
  query: string,
  options: SearchOptions = {}
): Promise<SearchResults<DTCategory_Public>> {
  return SearchGeneric(FBD_Category_Public, query, options);
}

/**
 * Searches for retailers.
 * @param query search query test e.g. 'tk max'
 * @returns an array of retailers
 * @example
 * SearchRetailers('tk max')
 */
export async function SearchRetailers(
  query: string,
  options: SearchOptions = {}
): Promise<SearchResults<DTRetailer_Public>> {
  return SearchGeneric(FBD_Retailer_Public, query, options);
}

/**
 * Searches for vendables.
 * @param query search query test e.g. 'sofa'
 * @returns an array of vendables
 * @example
 * SearchVendables('sofa')
 */
export async function SearchVendables(
  query: string,
  options: SearchOptions = {}
): Promise<SearchResults<DTVendable>> {
  //if (options.getCategoryData) {
  //  console.error(`options.getCategoryData IS DEPRECIATED`);
  //}
  return SearchGeneric(FBD_Vendable, query, options);
}

/**
 * Searches for vendables by UPC only.
 * @param query search query test e.g. '0000000000000'
 * @returns an array of vendables
 * @example
 * SearchVendables('sofa')
 */
export async function SearchVendablesByUpcCode(
  upc: string,
  options: SearchOptions = {}
): Promise<SearchResults<DTVendable>> {
  options.restrictToAttribues = ['payload.upc'];
  return SearchVendables(upc, options);
}

export async function SearchVendablesBySkuCode(
  sku: string,
  options: SearchOptions = {}
): Promise<SearchResults<DTVendable>> {
  options.restrictToAttribues = ['payload.mpn'];
  return SearchVendables(sku, options);
}

/**
 * Searches for vendables by id only.
 * @param query search query test e.g. '0000000000000'
 * @returns an array of vendables
 * @example
 * SearchVendablesByDocId('moto-abcd1239cx')
 */
export async function SearchVendablesByDocid(
  docid: string,
  options: SearchOptions = {}
): Promise<SearchResults<DTVendable>> {
  options.restrictToAttribues = ['payload.docid'];
  return SearchVendables(docid, options);
}

/**
 * Searches within a specific vendable facet.
 * Useful for polulating filter dropdowns.
 * Currently only supports colour and series.
 * @param facetName name of the facet to search within e.g. 'colour.name, series'
 * @param query search query test e.g. 'red'
 * @returns an array of facet items
 * @example
 * SearchFacet('colour','red')
 */
/*

// NOT USED RIGHT NOW

export async function SearchVendableFacet(
  facetName: string,
  query: string
): Promise<SearchResultsFacet> {
  const projectName = GetProjectName();
  return SearchForFacetValues(projectName,'vendable', facetName, query);
}
*/

export function GetUniqueCategoryList(vendables: DTVendable[]) {
  // Get all unique categories from results
  const categoriesList = vendables
    .map((result) => result.category)
    .filter(Boolean);

  // Merge all the arrays into one
  const mergedCategories = categoriesList.flat(1);

  // Remove duplicates
  const uniqueCategories = [...new Set(mergedCategories)];

  // Get item count for each category from results
  const categoryCounts = vendables.reduce((acc: any, result: any) => {
    if (result.category) {
      result.category.forEach((category: string) => {
        if (acc[category]) {
          acc[category] += 1;
        } else {
          acc[category] = 1;
        }
      });
    }
    return acc;
  }, {});

  const uniqueCategoryList = uniqueCategories.map((category, i) => {
    return {
      label: `${category?.[0].toUpperCase()}${category?.slice(1)}`,
      count: categoryCounts[`${category}`],
    };
  });

  return uniqueCategoryList;
};

export async function GetVendableByDocId(docId:string){
  const vendableData = await FBD_Vendable.query().where("docid","==",docId).getContents();
  return vendableData;
}

/**
 * Search for holding_proxy (registrations) - Basic version
 * @param query search query test e.g. '0000000000000'
 * @returns an array of holding_proxy
 * @example
 * SearchHoldingProxy('0000000000000')
 */
export async function SearchHoldingProxy(
  query: string,
  options: SearchOptions = {}
): Promise<SearchResults<DTHoldingProxy>> {
  const basicResults = await SearchGeneric(FBD_HoldingProxy, query, options);
  return createEnhancedSearchResults(basicResults, options);
}

/* -------------------------------------------------------------------------- */
/*                           Helper Functions                                 */
/* -------------------------------------------------------------------------- */

/**
 * Creates enhanced pagination metadata from any basic search results
 * Can be used by ANY search function in the system!
 * @param basicResults The basic search results from any SearchGeneric call
 * @param options The search options used
 * @returns Enhanced pagination metadata
 */
export function createEnhancedSearchResults<TYPE>(
  basicResults: SearchResults<TYPE>,
  options: SearchOptions
): EnhancedSearchResults<TYPE> {
  const recordsPerPage = options.hitsPerPage || 10;
  const currentPage1Based = basicResults.page + 1; // Convert to 1-based
  const hasNextPage = basicResults.page < basicResults.numpages - 1;
  const hasPreviousPage = basicResults.page > 0;
  
  // Calculate record range for current page
  const start = (basicResults.page * recordsPerPage) + 1;
  const end = Math.min(start + basicResults.items.length - 1, basicResults.numitems);

  const pagination = {
    totalRecords: basicResults.numitems,
    totalPages: basicResults.numpages,
    currentPage: currentPage1Based,
    recordsPerPage,
    recordsInThisPage: basicResults.items.length,
    hasNextPage,
    hasPreviousPage,
    nextPage: hasNextPage ? currentPage1Based + 1 : null,
    previousPage: hasPreviousPage ? currentPage1Based - 1 : null,
    pageRange: {
      start: basicResults.numitems > 0 ? start : 0,
      end: basicResults.numitems > 0 ? end : 0,
    }
  };

  return {
    ...basicResults,
    pagination
  };
}

/* -------------------------------------------------------------------------- */
/*                           Index Discovery Utilities                       */
/* -------------------------------------------------------------------------- */

// Re-export index discovery utilities for convenient access
export {
  getCachedIndexInfo,
  refreshIndexCache,
  getAllCachedIndexInfo,
  clearIndexCache,
  isFieldSortable,
  getReplicaIndexName,
  startPeriodicCacheRefresh,
  stopPeriodicCacheRefresh,
  getCacheStats,
} from './index-discovery';